import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { SparklesIcon, Building2, Wrench, TrendingUp, Home, Check, Hammer, ArrowRight } from 'lucide-react';

// Hero background image - Vue panoramique suisse représentant tous les pôles
const HERO_IMAGE = "https://images.pexels.com/photos/695210/pexels-photo-695210.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const HomePage = () => {
  const navigate = useNavigate();

  const poles = [
    {
      icon: SparklesIcon,
      title: 'Homely Nettoyage',
      description: 'Canapé, terrasse, vitres, fin de chantier, après bail : un nettoyage soigné pour retrouver un bien impeccable.',
      cta: 'Découvrir',
      link: '/nettoyage',
      image: 'https://images.pexels.com/photos/6195131/pexels-photo-6195131.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    },
    {
      icon: Building2,
      title: 'Homely Conciergerie',
      description: 'Photographie, annonce, pricing, gestion voyageurs, entretien : nous optimisons votre location saisonnière de A à Z.',
      cta: 'Découvrir',
      link: '/conciergerie',
      image: 'https://images.pexels.com/photos/4035566/pexels-photo-4035566.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    },
    {
      icon: Wrench,
      title: 'Homely Rénovation',
      description: 'Rénovation intérieure complète ou partielle avec coordination, suivi de chantier et finitions haut de gamme.',
      cta: 'Découvrir',
      link: '/renovation',
      image: 'https://images.unsplash.com/photo-1771371282665-545256b20dca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBraXRjaGVuJTIwcmVub3ZhdGlvbiUyMGx1eHVyeSUyMGhvbWV8ZW58MHx8fHwxNzc0MTE3MjE3fDA&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: Hammer,
      title: 'Homely Montage',
      description: 'Montage de meubles, agencement cuisine, transport et livraison, petites réparations Handy Man.',
      cta: 'Découvrir',
      link: '/montage',
      image: 'https://images.pexels.com/photos/4981803/pexels-photo-4981803.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    },
    {
      icon: TrendingUp,
      title: 'Homely Marketing',
      description: 'Identité visuelle, site web, SEO, Google My Business, IA et publicité : nous développons votre visibilité.',
      cta: 'Découvrir',
      link: '/marketing',
      image: 'https://images.unsplash.com/photo-1762558978967-29790ec47bbb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwbWFya2V0aW5nJTIwb2ZmaWNlJTIwbW9kZXJuJTIwd29ya3NwYWNlfGVufDB8fHx8MTc3NDExNzIxOXww&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: Home,
      title: 'Homely Immo',
      description: 'Estimation, mise en valeur, annonce, visites et accompagnement : nous vous aidons à vendre.',
      cta: 'Découvrir',
      link: '/immo',
      image: 'https://images.pexels.com/photos/36025719/pexels-photo-36025719.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    }
  ];

  const reassurancePoints = [
    'Un seul interlocuteur',
    'Intervention en Suisse romande',
    'Approche structurée',
    'Qualité d\'exécution',
    'Suivi sérieux',
    'Logique orientée résultat'
  ];

  const cities = ['Genève', 'Lausanne', 'Montreux', 'Villars', 'Verbier', 'Crans-Montana', 'Fribourg', 'Neuchâtel', 'Valais'];

  return (
    <div className="min-h-screen bg-background" data-testid="home-page">
      <Header />
      
      {/* Hero Section with Image */}
      <section className="hero-section relative min-h-[80vh] sm:min-h-[85vh] flex items-center">
        <img 
          src={HERO_IMAGE} 
          alt="Luxury Swiss property" 
          className="hero-image-bg"
        />
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
          <div className="max-w-3xl">
            <Badge className="mb-4 sm:mb-6 bg-white/20 text-white border-white/30 backdrop-blur-sm text-xs sm:text-sm">
              Services immobiliers haut de gamme
            </Badge>
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 text-white leading-tight" style={{ fontFamily: 'var(--font-heading)' }} data-testid="hero-title">
              Un seul partenaire pour votre bien immobilier en Suisse romande.
            </h1>
            <p className="text-sm sm:text-lg md:text-xl text-white/90 mb-6 sm:mb-10 max-w-2xl">
              Nettoyage, conciergerie Airbnb, rénovation, montage, marketing et vente immobilière : Homely Solutions coordonne tout pour valoriser votre bien.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button 
                size="lg" 
                className="btn-premium h-12 sm:h-14 px-6 sm:px-10 text-sm sm:text-base rounded-full w-full sm:w-auto"
                onClick={() => navigate('/contact')}
                data-testid="hero-primary-cta"
              >
                Demander un devis gratuit
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="h-12 sm:h-14 px-6 sm:px-10 text-sm sm:text-base rounded-full bg-white/10 border-white/30 text-white hover:bg-white hover:text-anthracite backdrop-blur-sm w-full sm:w-auto"
                onClick={() => document.getElementById('poles').scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-secondary-cta"
              >
                Découvrir nos services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Reassurance Bar */}
      <section className="py-4 sm:py-6 border-b" style={{ backgroundColor: 'var(--sage)', color: 'white' }}>
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-x-4 sm:gap-x-8 gap-y-2 sm:gap-y-3">
            {reassurancePoints.map((point, idx) => (
              <div key={idx} className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-medium">
                <Check className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                <span>{point}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Orientation */}
      <section className="py-12 sm:py-20 gradient-premium">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Quel est votre besoin ?
            </h2>
            <div className="section-divider"></div>
          </div>
          
          <Tabs defaultValue="nettoyer" className="w-full" data-testid="quick-orientation">
            <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 mb-8 h-auto bg-white shadow-sm rounded-xl p-1 gap-1">
              <TabsTrigger value="nettoyer" className="py-2.5 px-2 text-xs sm:text-sm rounded-lg data-[state=active]:bg-[var(--sage)] data-[state=active]:text-white">Nettoyer</TabsTrigger>
              <TabsTrigger value="louer" className="py-2.5 px-2 text-xs sm:text-sm rounded-lg data-[state=active]:bg-[var(--sage)] data-[state=active]:text-white">Gérer location</TabsTrigger>
              <TabsTrigger value="renover" className="py-2.5 px-2 text-xs sm:text-sm rounded-lg data-[state=active]:bg-[var(--sage)] data-[state=active]:text-white">Rénover</TabsTrigger>
              <TabsTrigger value="monter" className="py-2.5 px-2 text-xs sm:text-sm rounded-lg data-[state=active]:bg-[var(--sage)] data-[state=active]:text-white">Monter / Réparer</TabsTrigger>
              <TabsTrigger value="marketer" className="py-2.5 px-2 text-xs sm:text-sm rounded-lg data-[state=active]:bg-[var(--sage)] data-[state=active]:text-white">Marketing</TabsTrigger>
              <TabsTrigger value="vendre" className="py-2.5 px-2 text-xs sm:text-sm rounded-lg data-[state=active]:bg-[var(--sage)] data-[state=active]:text-white">Vendre</TabsTrigger>
            </TabsList>
            
            <TabsContent value="nettoyer" className="text-center space-y-3 sm:space-y-4 px-2">
              <p className="text-sm sm:text-base md:text-lg text-muted-foreground">Canapé, terrasse, vitres, fin de chantier ou après bail : confiez-nous le nettoyage de votre bien.</p>
              <Button onClick={() => navigate('/nettoyage')} className="btn-premium rounded-full px-6 sm:px-8 text-sm sm:text-base">Voir les services de nettoyage</Button>
            </TabsContent>
            
            <TabsContent value="louer" className="text-center space-y-3 sm:space-y-4 px-2">
              <p className="text-sm sm:text-base md:text-lg text-muted-foreground">Optimisez votre location saisonnière avec notre service de conciergerie complet.</p>
              <Button onClick={() => navigate('/conciergerie')} className="btn-premium rounded-full px-6 sm:px-8 text-sm sm:text-base">Découvrir la conciergerie</Button>
            </TabsContent>
            
            <TabsContent value="renover" className="text-center space-y-3 sm:space-y-4 px-2">
              <p className="text-sm sm:text-base md:text-lg text-muted-foreground">Rénovation complète ou partielle avec coordination et suivi de chantier.</p>
              <Button onClick={() => navigate('/renovation')} className="btn-premium rounded-full px-6 sm:px-8 text-sm sm:text-base">Découvrir la rénovation</Button>
            </TabsContent>
            
            <TabsContent value="monter" className="text-center space-y-3 sm:space-y-4 px-2">
              <p className="text-sm sm:text-base md:text-lg text-muted-foreground">Montage de meubles, agencement cuisine, transport, petites réparations : nos Handy Man interviennent rapidement.</p>
              <Button onClick={() => navigate('/montage')} className="btn-premium rounded-full px-6 sm:px-8 text-sm sm:text-base">Découvrir le montage</Button>
            </TabsContent>
            
            <TabsContent value="marketer" className="text-center space-y-3 sm:space-y-4 px-2">
              <p className="text-sm sm:text-base md:text-lg text-muted-foreground">Augmentez votre visibilité et générez plus de leads avec nos solutions marketing.</p>
              <Button onClick={() => navigate('/marketing')} className="btn-premium rounded-full px-6 sm:px-8 text-sm sm:text-base">Développer mon activité</Button>
            </TabsContent>
            
            <TabsContent value="vendre" className="text-center space-y-3 sm:space-y-4 px-2">
              <p className="text-sm sm:text-base md:text-lg text-muted-foreground">Vendez dans les meilleures conditions avec notre accompagnement immobilier complet.</p>
              <Button onClick={() => navigate('/immo')} className="btn-premium rounded-full px-6 sm:px-8 text-sm sm:text-base">Découvrir Homely Immo</Button>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* 6 Poles of Expertise - Premium Cards with Images */}
      <section className="py-12 sm:py-16 md:py-24" id="poles">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <Badge className="mb-3 sm:mb-4 text-xs sm:text-sm" style={{ backgroundColor: 'var(--sage)', color: 'white' }}>Nos expertises</Badge>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Nos 6 pôles d'expertise
            </h2>
            <div className="section-divider"></div>
            <p className="text-sm sm:text-base md:text-lg text-muted-foreground max-w-2xl mx-auto px-4">
              Une équipe dédiée pour chaque domaine, un seul interlocuteur pour vous.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {poles.map((pole, idx) => (
              <Card 
                key={idx} 
                className="card-premium overflow-hidden cursor-pointer group"
                onClick={() => navigate(pole.link)}
                data-testid={`pole-card-${idx}`}
              >
                {/* Image */}
                <div className="relative h-36 sm:h-44 md:h-48 overflow-hidden">
                  <img 
                    src={pole.image} 
                    alt={pole.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4">
                    <pole.icon className="h-8 w-8 sm:h-10 sm:w-10 text-white drop-shadow-lg" />
                  </div>
                </div>
                
                <CardHeader className="pb-2 px-4 sm:px-6">
                  <CardTitle className="text-lg sm:text-xl">{pole.title}</CardTitle>
                </CardHeader>
                <CardContent className="px-4 sm:px-6">
                  <CardDescription className="text-sm sm:text-base mb-4">{pole.description}</CardDescription>
                  <Button 
                    variant="outline" 
                    className="w-full group-hover:bg-[var(--sage)] group-hover:text-white group-hover:border-[var(--sage)] transition-all"
                  >
                    {pole.cta}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Cities Section */}
      <section className="py-20 gradient-premium-dark">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            Nous intervenons dans toute la Suisse romande
          </h2>
          <div className="w-16 h-1 bg-[var(--sage)] mx-auto mb-8 rounded-full"></div>
          <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto">
            {cities.map((city, idx) => (
              <Badge 
                key={idx} 
                variant="secondary" 
                className="px-5 py-2 text-sm bg-white/10 text-white border border-white/20 hover:bg-white/20 transition-colors cursor-default"
              >
                {city}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-24">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl" style={{ backgroundColor: 'var(--sage)' }}>
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{ 
                backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px)',
                backgroundSize: '30px 30px'
              }}></div>
            </div>
            <div className="relative px-8 py-16 sm:px-16 sm:py-20 text-center text-white">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                Prêt à valoriser votre bien ?
              </h2>
              <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
                Contactez-nous pour un devis gratuit et sans engagement. Notre équipe vous répond sous 24h.
              </p>
              <Button 
                size="lg" 
                className="h-14 px-10 text-base rounded-full bg-white text-[var(--anthracite)] hover:bg-white/90"
                onClick={() => navigate('/contact')}
              >
                Demander un devis gratuit
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
